export function useEventListeners(context) {
	const listeners = new Map();

	function addListener(target, event, handler) {
		const boundHandler = handler.bind(context);
		listeners.set(handler, { target, event, boundHandler });
		target.addEventListener(event, boundHandler);
	}

	function removeListener(target, event, handler) {
		const entry = listeners.get(handler);
		if (entry) {
			target.removeEventListener(event, entry.boundHandler);
			listeners.delete(handler);
		}
	}

	function removeAllListeners() {
		for (const { target, event, boundHandler } of listeners.values()) {
			target.removeEventListener(event, boundHandler);
		}
		listeners.clear();
	}

	return {
		addListener,
		removeListener,
		removeAllListeners,
	};
}

/**
 *
 * @param {Event} e
 * @param {String} selector
 * @returns {HTMLElement|null}
 */
export function getElementFromEvent(e, selector) {
	if (!e.target) {
		return null;
	}

	const el = e.target?.closest(selector);

	if (!el) {
		return null;
	}

	return el;
}

export function getKeyboardFocusableElements(element = document) {
	return [
		...element.querySelectorAll('a, button, input, textarea, select, details,[tabindex]:not([tabindex="-1"])'),
	].filter((el) => !el.hasAttribute('disabled') && !el.getAttribute('aria-hidden'));
}

export function trapFocus(element, focusableElements) {
	if (focusableElements.length === 0) {
		return null;
	}

	let currentIndex = 0;

	focusableElements[currentIndex].focus();

	function handleTab(e) {
		const isTabPressed = e.key === 'Tab';

		if (!isTabPressed) {
			return;
		}

		let newIndex;

		if (e.shiftKey) {
			newIndex = (currentIndex - 1 + focusableElements.length) % focusableElements.length;
		} else {
			newIndex = (currentIndex + 1) % focusableElements.length;
		}

		focusableElements[newIndex].focus();
		currentIndex = newIndex;

		e.preventDefault();
	}

	element.addEventListener('keydown', handleTab);

	return {
		remove: function removeTrapFocus() {
			element.removeEventListener('keydown', handleTab);
		},
		focus: function focusTrapFocus() {
			focusableElements[currentIndex].focus();
		},
	};
}

export function getViewPercentage(element) {
	const viewport = {
		top: window.scrollY,
		bottom: window.scrollY + window.innerHeight,
	};

	const elementBoundingRect = element.getBoundingClientRect();
	const elementPos = {
		top: elementBoundingRect.y + window.scrollY,
		bottom: elementBoundingRect.y + elementBoundingRect.height + window.scrollY,
	};

	if (viewport.top > elementPos.bottom || viewport.bottom < elementPos.top) {
		return 0;
	}

	// Element is fully within viewport
	if (viewport.top < elementPos.top && viewport.bottom > elementPos.bottom) {
		return 100;
	}

	// Element is bigger than the viewport
	if (elementPos.top < viewport.top && elementPos.bottom > viewport.bottom) {
		return 100;
	}

	const elementHeight = elementBoundingRect.height;
	let elementHeightInView = elementHeight;

	if (elementPos.top < viewport.top) {
		elementHeightInView = elementHeight - (window.scrollY - elementPos.top);
	}

	if (elementPos.bottom > viewport.bottom) {
		elementHeightInView = elementHeightInView - (elementPos.bottom - viewport.bottom);
	}

	const percentageInView = (elementHeightInView / window.innerHeight) * 100;

	return Math.round(percentageInView);
}

export function isLocalStorageEnabled() {
	try {
		const key = `__storage__test`;
		window.localStorage.setItem(key, null);
		window.localStorage.removeItem(key);
		return true;
	} catch (e) {
		return false;
	}
}

export function track(event, data = {}) {
	if (!window.plausible) {
		return;
	}

	window.plausible(event, data);

	/*
		Events:
	
		Click event pop-up
		Open event
		Click collaborate nav button
		Switch language
		Play video
		Click cta button

		Properties:

		title
	*/
}
